import { useEffect, useMemo, useState } from "react";
import { Award, Eye, Layers } from "lucide-react";
import { API_EMPLOYEE_CERTIFICATION } from "../../../config/Endpoints.js";
import { Card } from "../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { Badge, Button, ResponsiveDataTable, Spinner, Tabs, TabsContent, TabsList, TabsTrigger, type ResponsiveColumn } from "../../components/ui/index.ts";
import { CertificationDocumentViewer } from "../../components/features/certifications/certification-document-viewer.tsx";
import { useCertificationDocument } from "../../components/features/certifications/use-certification-document.ts";
import { useTechStackList } from "../tech-stack-list/use-tech-stack-list.ts";
import type { EmployeeCertification, EmployeeCertificationRow } from "../tech-stack-list/certifications/certification-types.ts";
import { getApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError } from "../../utils/toast.ts";
import { formatEnumLabel, formatToLongDate } from "../../utils/helpers.ts";
import styles from "./employee-skills-tabs.module.css";

function TechStackDetails({ employeeId }: { employeeId: string }) {
  const filters = useMemo(() => ({ employeeId, categoryId: "", techStackId: "", techStackAssigned: "", technicalStatus: "", location: "" }), [employeeId]);
  const { rows, loading, error } = useTechStackList(filters, 1, 10);
  useEffect(() => { if (error) showError(error); }, [error]);
  const skills = rows.find((row) => String(row.employeeId) === employeeId)?.techStacks ?? [];

  if (loading) return <Spinner label="Loading..." />;
  if (error) return <EmptyStateMessage message="Unable to load technical skills." />;
  if (!skills.length) return <EmptyStateMessage message="No Technical Skills Assigned." />;
  return <ul className={styles.skills}>
    {skills.map((skill) => <li key={skill.techStackId} className={styles.skill}>
      <span className={styles.dot} aria-hidden />
      {skill.techStack}
      {skill.primarySkill && <Badge variant="success">Primary</Badge>}
    </li>)}
  </ul>;
}

type CertificationRow = EmployeeCertification & { id: string } & Record<string, unknown>;

function CertificationDetails({ employeeId }: { employeeId: string }) {
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const viewer = useCertificationDocument(accessToken());

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setRows([]);
    getApi<EmployeeCertificationRow | { data: EmployeeCertificationRow }>(
      `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(employeeId)}`,
      accessToken(), "json", controller.signal,
    ).then(({ data }) => {
      if (controller.signal.aborted) return;
      const payload = (data.data ?? data) as EmployeeCertificationRow;
      setRows((payload.certifications ?? []).map((item, index) => ({
        ...item, id: String(item.employeeCertificationId ?? `${item.certificationId}-${index}`),
      })));
    }).catch(() => {
      if (!controller.signal.aborted) {
        setError(true);
        showError("Unable to load employee certifications.");
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [employeeId]);

  const columns: ResponsiveColumn<CertificationRow>[] = [
    { id: "certification", header: "Certification Name", accessor: "certification" },
    { id: "category", header: "Category", render: (row) => formatEnumLabel(row.category ?? "-") },
    { id: "certificateNumber", header: "Certificate No.", accessor: "certificateNumber" },
    { id: "issueDate", header: "Issued On", render: (row) => formatToLongDate(row.issueDate) },
    { id: "expiryDate", header: "Expiry On", render: (row) => formatToLongDate(row.expiryDate) },
    { id: "verified", header: "Status", render: (row) => <Badge variant={row.verified ? "success" : "warning"}>{row.verified ? "Verified" : "Not Verified"}</Badge> },
    { id: "document", header: "Document", render: (row) => row.documentPath ? (
      <Button size="sm" variant="accent-ghost" startIcon={<Eye size={16} aria-hidden />}
        disabled={Boolean(viewer.loadingPath)} onClick={() => void viewer.view(row.documentPath!)}>
        {viewer.loadingPath === row.documentPath ? "Loading..." : "View"}
      </Button>
    ) : /^https?:\/\//i.test(row.certificateUrl ?? "") ? (
      <a href={row.certificateUrl} target="_blank" rel="noopener noreferrer">View</a>
    ) : "-" },
  ];

  if (loading) return <Spinner label="Loading certifications" />;
  if (error) return <EmptyStateMessage message="Unable to load certifications." />;
  return <>
    <ResponsiveDataTable<CertificationRow> columns={columns} data={rows} aria-label="Employee certifications"
      emptyState={<EmptyStateMessage message="No certifications uploaded." />} />
    <CertificationDocumentViewer document={viewer.document} onClose={viewer.close} />
  </>;
}

export function EmployeeSkillsTabs({ employeeId }: { employeeId: string }) {
  const [tab, setTab] = useState("tech-stack");
  return <Card className={styles.card}>
    <Tabs value={tab} onValueChange={setTab} idPrefix="employee-profile-skills">
      <TabsList>
        <TabsTrigger value="tech-stack"><Layers size={18} aria-hidden /> Technical Skills</TabsTrigger>
        <TabsTrigger value="certifications"><Award size={18} aria-hidden /> Certifications</TabsTrigger>
      </TabsList>
      <TabsContent value="tech-stack" className={styles.panel}>
        <TechStackDetails employeeId={employeeId} />
      </TabsContent>
      <TabsContent value="certifications" className={styles.panel}>
        <CertificationDetails employeeId={employeeId} />
      </TabsContent>
    </Tabs>
  </Card>;
}
